// src/pages/ServiceDashboard.jsx
// Service Burden Dashboard - Analytics and follow-up management

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { csrfHeadersFor } from '../api';
import {
  Phone,
  PhoneCall,
  Clock,
  CheckCircle,
  AlertTriangle,
  Calendar,
  TrendingUp,
  Users,
  RefreshCw,
  ChevronRight,
  BarChart3,
  PieChart,
  Activity,
  Timer,
  Flag,
  XCircle,
  Search,
  Headphones,
  ArrowRight
} from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

// Call reason display
const CALL_REASONS = {
  claims_issue: { label: 'Claims Issue', color: 'red' },
  billing: { label: 'Billing', color: 'orange' },
  prior_auth: { label: 'Prior Auth', color: 'purple' },
  enrollment_status: { label: 'Enrollment', color: 'blue' },
  id_card: { label: 'ID Card', color: 'cyan' },
  benefits_question: { label: 'Benefits', color: 'teal' },
  provider_search: { label: 'Provider', color: 'green' },
  rx_issue: { label: 'Rx Issue', color: 'pink' },
  appeal: { label: 'Appeal', color: 'red' },
  disenrollment: { label: 'Disenroll', color: 'slate' },
  other: { label: 'Other', color: 'gray' }
};

// Outcome display
const OUTCOMES = {
  resolved: { label: 'Resolved', color: 'green', icon: CheckCircle },
  pending: { label: 'Pending', color: 'yellow', icon: Clock },
  escalated: { label: 'Escalated', color: 'orange', icon: TrendingUp },
  callback_scheduled: { label: 'Callback', color: 'blue', icon: Calendar },
  no_answer: { label: 'No Answer', color: 'red', icon: XCircle }
};

export default function ServiceDashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [followUpFilter, setFollowUpFilter] = useState('pending');

  // Client search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimer = useRef(null);

  // Search clients with debounce
  const searchClients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(`${BASE}/clients?search=${encodeURIComponent(query)}&limit=8`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.data || []);
        setShowResults(true);
      }
    } catch {
      // ignore
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchClients(val), 300);
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load analytics and follow-ups
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [analyticsRes, followUpsRes] = await Promise.all([
          fetch(`${BASE}/service/analytics?days=${period}`, { credentials: 'include' }),
          fetch(`${BASE}/service/follow-ups?status=${followUpFilter}`, { credentials: 'include' })
        ]);

        if (analyticsRes.ok) {
          const data = await analyticsRes.json();
          setAnalytics(data.analytics || null);
        }
        if (followUpsRes.ok) {
          const data = await followUpsRes.json();
          setFollowUps(data.followUps || []);
        }
      } catch (err) {
        console.error('Failed to load service dashboard:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [period, followUpFilter]);

  // Complete a follow-up
  async function completeFollowUp(eventId, notes) {
    try {
      const res = await fetch(`${BASE}/service/events/${eventId}/complete-follow-up`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        credentials: 'include',
        body: JSON.stringify({ notes })
      });

      if (res.ok) {
        setFollowUps(followUps.filter(f => f.id !== eventId));
      }
    } catch (err) {
      console.error('Failed to complete follow-up:', err);
    }
  }

  // Format date
  function formatDate(dateStr) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Check if overdue
  function isOverdue(dateStr) {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  }

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg">
              <PhoneCall size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-[#172A3A]">Service Dashboard</h1>
              <p className="text-slate-500">Track carrier calls, outcomes, and follow-ups</p>
            </div>
          </div>
        </div>

        {/* Client Search — Launch Workspace */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 mb-8" ref={searchRef}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
              <Headphones size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#172A3A]">Open Service Workspace</h2>
              <p className="text-sm text-slate-500">Search for a client to start or resume a service session</p>
            </div>
          </div>
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => { if (searchResults.length > 0) setShowResults(true); }}
              placeholder="Search by client name, phone, or Medicare ID..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
            />
            {searching && (
              <RefreshCw size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-400 animate-spin" />
            )}
            {showResults && searchResults.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-80 overflow-y-auto">
                {searchResults.map(c => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setShowResults(false);
                      setSearchQuery('');
                      navigate(`/service/client/${c.id}`);
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-indigo-50 transition-colors text-left border-b border-slate-100 last:border-b-0"
                  >
                    <div>
                      <span className="font-semibold text-sm text-[#172A3A]">
                        {c.firstName} {c.lastName}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        {c.carrier && <span>{c.carrier}</span>}
                        {c.planType && <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-medium">{c.planType}</span>}
                        {c.phone && <span>{c.phone}</span>}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-indigo-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
            {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl p-4 text-center text-sm text-slate-500">
                No clients found for "{searchQuery}"
              </div>
            )}
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-slate-500">Time Period:</span>
          {[7, 30, 90].map(days => (
            <button
              key={days}
              onClick={() => setPeriod(days)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                period === days
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-md p-5 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Phone className="text-indigo-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Calls</p>
                <p className="text-2xl font-bold text-[#172A3A]">{analytics?.total_events || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-5 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Flag className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Follow-ups</p>
                <p className="text-2xl font-bold text-[#172A3A]">{analytics?.pending_follow_ups || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-5 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{analytics?.overdue_follow_ups || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-5 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Timer className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg Resolution</p>
                <p className="text-2xl font-bold text-[#172A3A]">
                  {analytics?.avg_resolution_minutes
                    ? `${Math.round(analytics.avg_resolution_minutes)}m`
                    : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Follow-ups Section */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-[#172A3A] flex items-center gap-2">
                  <Calendar className="text-indigo-500" size={20} />
                  Follow-ups
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFollowUpFilter('pending')}
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      followUpFilter === 'pending'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => setFollowUpFilter('completed')}
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      followUpFilter === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Completed
                  </button>
                </div>
              </div>
            </div>

            <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
              {followUps.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-slate-500">
                    {followUpFilter === 'pending' ? 'No pending follow-ups!' : 'No completed follow-ups'}
                  </p>
                </div>
              ) : (
                followUps.map(item => (
                  <div key={item.id} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Link
                            to={`/service/client/${item.client_id}`}
                            className="font-semibold text-[#172A3A] hover:text-indigo-600"
                          >
                            {item.first_name} {item.last_name}
                          </Link>
                          <span className="text-xs text-slate-400">
                            {item.carrier_code || item.carrier_name}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-1">
                          {CALL_REASONS[item.call_reason]?.label || item.call_reason}
                          {item.follow_up_reason && ` - ${item.follow_up_reason}`}
                        </p>
                        <div className="flex items-center gap-3 text-xs">
                          <span className={`flex items-center gap-1 ${isOverdue(item.follow_up_date) ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                            <Calendar size={12} />
                            {formatDate(item.follow_up_date)}
                            {isOverdue(item.follow_up_date) && ' (Overdue)'}
                          </span>
                        </div>
                      </div>
                      {followUpFilter === 'pending' && (
                        <button
                          onClick={() => completeFollowUp(item.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 text-xs font-medium rounded-lg transition-colors"
                        >
                          <CheckCircle size={14} />
                          Complete
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Outcomes Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-5">
              <h3 className="font-bold text-[#172A3A] mb-4 flex items-center gap-2">
                <PieChart className="text-indigo-500" size={18} />
                Outcomes
              </h3>
              <div className="space-y-3">
                {analytics?.events_by_outcome?.map(item => {
                  const outcome = OUTCOMES[item.outcome];
                  const Icon = outcome?.icon || Activity;
                  const percent = analytics.total_events > 0
                    ? Math.round((item.count / analytics.total_events) * 100)
                    : 0;

                  return (
                    <div key={item.outcome} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg bg-${outcome?.color || 'gray'}-100 flex items-center justify-center`}>
                        <Icon size={16} className={`text-${outcome?.color || 'gray'}-600`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-slate-700">{outcome?.label || item.outcome}</span>
                          <span className="text-slate-500">{item.count}</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full mt-1">
                          <div
                            className={`h-full bg-${outcome?.color || 'gray'}-500 rounded-full`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Carriers */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-5">
              <h3 className="font-bold text-[#172A3A] mb-4 flex items-center gap-2">
                <BarChart3 className="text-indigo-500" size={18} />
                Top Carriers Called
              </h3>
              <div className="space-y-2">
                {analytics?.top_carriers?.slice(0, 5).map((carrier, idx) => (
                  <div key={carrier.carrier_name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="text-slate-700">{carrier.carrier_name}</span>
                    </div>
                    <span className="font-medium text-[#172A3A]">{carrier.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* High-Touch Clients */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-5">
              <h3 className="font-bold text-[#172A3A] mb-4 flex items-center gap-2">
                <Users className="text-indigo-500" size={18} />
                High-Touch Clients
              </h3>
              <div className="space-y-2">
                {analytics?.high_touch_clients?.slice(0, 5).map(client => (
                  <Link
                    key={client.client_id}
                    to={`/service/client/${client.client_id}`}
                    className="flex items-center justify-between text-sm hover:bg-slate-50 p-2 -mx-2 rounded-lg transition-colors"
                  >
                    <span className="text-slate-700">{client.first_name} {client.last_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-indigo-600">{client.event_count} calls</span>
                      <ChevronRight size={14} className="text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Call Reasons */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-5">
              <h3 className="font-bold text-[#172A3A] mb-4 flex items-center gap-2">
                <Activity className="text-indigo-500" size={18} />
                Top Issues
              </h3>
              <div className="flex flex-wrap gap-2">
                {analytics?.call_reasons?.slice(0, 6).map(reason => {
                  const reasonInfo = CALL_REASONS[reason.call_reason];
                  return (
                    <span
                      key={reason.call_reason}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full bg-${reasonInfo?.color || 'gray'}-100 text-${reasonInfo?.color || 'gray'}-700`}
                    >
                      {reasonInfo?.label || reason.call_reason} ({reason.count})
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
